/**
 * RotationSchemaView — the simple visual story of one rotation: per pool, a
 * header (name · layer · active before → after / cap) and one SWAP row per
 * change (OUT ⟶ IN), then blocked entries. Renders both the live
 * preview/apply result AND every history entry — one schema everywhere.
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';
import type {
  RotationElementDiff,
  RotationPoolSummary,
} from '@trafficpulse/shared';

import { cn } from '@/lib/utils';

import { layerIcon } from '../queue-icons';
import { layerLabel } from '../queue-status.util';
import { buildRotationSchema, swapsByPool, type RotationSwap } from './rotation-swaps';
import { RotationBlockedRow } from './blocked';
import { RotationSwapRow } from './RotationSwapRow';

const PoolHeader: FC<{
  swap: RotationSwap;
  pool?: RotationPoolSummary;
}> = ({ swap, pool }) => {
  const Icon = layerIcon(swap.layer);
  const over = pool?.cap != null && pool.activeAfter > pool.cap;
  return (
    <p className="flex items-center gap-1.5 text-[11px] font-semibold text-muted-foreground">
      <Icon className="h-3 w-3 shrink-0" aria-hidden />
      <span className="text-foreground">{swap.poolName}</span>
      <span>· {layerLabel(swap.layer)}</span>
      {pool ? (
        <span className="tabular-nums">
          · {pool.activeBefore} →{' '}
          <span className={cn(over && 'font-bold text-destructive')}>{pool.activeAfter}</span> /{' '}
          {pool.cap ?? '∞'}
        </span>
      ) : null}
    </p>
  );
};

export interface RotationSchemaViewProps {
  diffs: RotationElementDiff[];
  perPool: RotationPoolSummary[];
}

/** A titled list of blocked/ignored rows; tone follows the reason (neutral for
 *  no-stats, blue for a swap waiting on the queue, amber for genuine
 *  pool-floor / capacity blocks). */
const SECTION_TONE = {
  danger: 'text-destructive',
  warning: 'text-warning',
  primary: 'text-primary',
  muted: 'text-muted-foreground',
} as const;

const BlockedSection: FC<{
  label: string;
  tone: keyof typeof SECTION_TONE;
  diffs: RotationElementDiff[];
}> = ({ label, tone, diffs }) =>
  diffs.length === 0 ? null : (
    <div className="space-y-1.5">
      <p
        className={cn(
          'text-[11px] font-semibold uppercase tracking-wide',
          SECTION_TONE[tone],
        )}
      >
        {label} ({diffs.length})
      </p>
      {diffs.map((diff, i) => (
        <RotationBlockedRow key={`${diff.bankId}:${i}`} diff={diff} />
      ))}
    </div>
  );

const RotationSchemaView: FC<RotationSchemaViewProps> = ({ diffs, perPool }) => {
  const schema = buildRotationSchema(diffs);
  const groups = swapsByPool(schema.swaps);
  if (groups.length === 0 && schema.blocked.length === 0 && schema.caps.length === 0) {
    return (
      <p className="rounded-md border border-dashed border-border/70 px-3 py-2 text-[11px] text-muted-foreground">
        No changes — every live element passes the rules and fits the caps.
      </p>
    );
  }
  const summaryFor = (swap: RotationSwap) =>
    perPool.find((p) => p.layer === swap.layer && p.poolId === swap.poolId);
  const unlinked = schema.blocked.filter((b) => b.unmanaged);
  const ignored = schema.blocked.filter((b) => b.noStats && !b.unmanaged);
  const pairHolds = schema.blocked.filter((b) => b.pairHold && !b.unmanaged);
  const held = schema.blocked.filter(
    (b) => !b.noStats && !b.pairHold && !b.unmanaged,
  );
  return (
    <div className="space-y-3">
      {groups.map((swaps) => (
        <div key={`${swaps[0].layer}:${swaps[0].poolId}`} className="space-y-1.5">
          <PoolHeader swap={swaps[0]} pool={summaryFor(swaps[0])} />
          {swaps.map((swap) => (
            <RotationSwapRow key={swap.key} swap={swap} />
          ))}
        </div>
      ))}
      <BlockedSection label="Partner caps" tone="warning" diffs={schema.caps} />
      <BlockedSection
        label="Over capacity — not linked to the bank"
        tone="danger"
        diffs={unlinked}
      />
      <BlockedSection
        label="Waiting for a replacement"
        tone="primary"
        diffs={pairHolds}
      />
      <BlockedSection label="Blocked" tone="warning" diffs={held} />
      <BlockedSection label="Ignored — no stats" tone="muted" diffs={ignored} />
    </div>
  );
};

export default RotationSchemaView;
